import React, { useEffect, useState } from 'react';
import {
  RCTCategoryContainer,
  RCTCategoryTitleContainer,
  RCTFeatureContent,
  RCTFeatureTitle,
  RCTTableContentContainer,
} from './RCTTableContent.styles';
import { Icon } from '@legacy-vfuk/core-icon';
import { RCTTableContentProps } from './RCTTableContent.types';

type RCTFeatureContentProps = RCTTableContentProps['categories'][number]['features'][number]['featureContents'][number];
type RCTFeatureProps = RCTTableContentProps['categories'][number]['features'][number];

export const RCTTableContent = ({ products, isExpanded, categories }: RCTTableContentProps) => {
  const [openCategories, setOpenCategories] = useState<boolean[]>(categories.map(() => false));

  const expandAllCategories = () => {
    setOpenCategories(categories.map(() => true));
  };

  const collapseAllCategories = () => {
    setOpenCategories(categories.map(() => false));
  };

  useEffect(() => {
    isExpanded ? expandAllCategories() : collapseAllCategories();
  }, [isExpanded]);

  // Helper function to get the column index for a product
  const getProductColumnIndex = (productId: string): number => {
    const index = products.findIndex(product => product.productId === productId);
    return index >= 0 ? index : 0; // Default to first column if product not found
  };

  // Helper function to render feature content in the correct column
  const renderFeatureContentInColumns = (feature: RCTFeatureProps, categoryIndex: number, featureIndex: number) => {
    // Create an array to hold content for each product column
    const columnContent = new Array(products.length).fill(null);

    // Place each feature content in its correct column
    feature.featureContents.forEach((featureContent: RCTFeatureContentProps, contentIndex: number) => {
      const columnIndex = getProductColumnIndex(featureContent.productId);
      columnContent[columnIndex] = featureContent;
    });

    return columnContent.map((content, columnIndex) => (
      <RCTFeatureContent
        key={`content-${categoryIndex}-${featureIndex}-${columnIndex}`}
        className="feature-content"
        justifyItems={content?.featureContentType === 'checkmark' ? 'center' : undefined}
      >
        {content ? (
          <>
            {content.featureContentText && <p>{content.featureContentText}</p>}
            {content.featureContentLink && <a href={content.featureContentLink}>{content.featureContentLinkText}</a>}
            {content.featureContentType === 'checkmark' && (
              <Icon key={content.productId} name={'success'} group={'state'} size={4} />
            )}
          </>
        ) : (
          <p></p> // Empty cell for columns without content
        )}
      </RCTFeatureContent>
    ));
  };

  return (
    <React.Fragment>
      {categories.map((category, categoryIndex) => (
        <React.Fragment key={`category-${category.categoryTitle}`}>
          <RCTCategoryContainer
            onClick={() => setOpenCategories(prev => [...prev.slice(0, categoryIndex), !prev[categoryIndex]])}
            isOpen={openCategories[categoryIndex]}
          >
            <RCTCategoryTitleContainer>
              <p>{category.categoryTitle}</p>
              <Icon
                size={3}
                name={openCategories[categoryIndex] ? 'chevron-up' : 'chevron-down'}
                group="system"
                appearance="secondary"
              />
            </RCTCategoryTitleContainer>
          </RCTCategoryContainer>
          {category.features.map((feature, featureIndex) => {
            const isLastFeatureInCategory = featureIndex === category.features.length - 1;
            const isOpen = openCategories[categoryIndex];
            return (
              <React.Fragment key={`feature-${categoryIndex}-${featureIndex}`}>
                {isOpen && (
                  <RCTTableContentContainer
                    key={`feature-${categoryIndex}-${featureIndex}`}
                    className={isLastFeatureInCategory ? 'last-feature-in-category' : ''}
                    productCount={products.length}
                  >
                    <RCTFeatureTitle className="feature-title">
                      <p>{feature.featureTitle}</p>
                    </RCTFeatureTitle>
                    {renderFeatureContentInColumns(feature, categoryIndex, featureIndex)}
                  </RCTTableContentContainer>
                )}
              </React.Fragment>
            );
          })}
        </React.Fragment>
      ))}
    </React.Fragment>
  );
};
